import { useFrameLoader } from '@src/hooks/useFrameLoader';
import { LogManager } from '@src/utility/Logger';
import { useCallback, useEffect, useRef, useState } from 'react';

const logger = LogManager.getLogger('hooks:useSpeakerFrameThumbnails');

/** How long to wait for the frame worker before reporting it never answered. */
const WORKER_RESPONSE_TIMEOUT_MS = 15000;

/**
 * Load a video frame thumbnail for each anonymous speaker.
 *
 * Takes the representative frame number per speaker (see
 * `getThumbnailFrameNumbersBySpeaker`) and resolves it to a frame URL through
 * the same worker-backed loader the timeline thumbnails use, so frames already
 * fetched for the timeline come straight from its cache.
 *
 * `/api/video/frames` keys thumbnails off the story alone, so the story id
 * doubles as the loader's video id.
 *
 * Returns a map of speaker label -> frame URL; labels whose frame has not
 * loaded (or failed) are simply absent.
 */
export function useSpeakerFrameThumbnails(
  storyId: string | undefined,
  frameNumbersBySpeaker: Record<string, number>,
): Record<string, string> {
  const [thumbnails, setThumbnails] = useState<Record<string, string>>({});

  // Read the current speaker -> frame map inside a stable callback, so
  // `loadFrames` (memoized on `onFramesLoaded`) keeps its identity.
  const frameNumbersRef = useRef(frameNumbersBySpeaker);
  frameNumbersRef.current = frameNumbersBySpeaker;

  // Cleared by whichever worker message arrives first, so the watchdog only
  // fires when nothing came back at all.
  const watchdogRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearWatchdog = useCallback(() => {
    if (watchdogRef.current) {
      clearTimeout(watchdogRef.current);
      watchdogRef.current = null;
    }
  }, []);

  const handleFramesLoaded = useCallback(
    (frames: Array<{ frameNumber: number; url: string }>) => {
      clearWatchdog();

      const urlByFrameNumber = new Map(
        frames.map(frame => [frame.frameNumber, frame.url]),
      );
      const requested = frameNumbersRef.current;

      logger.debug(
        `Frame worker returned ${frames.length} frame(s): [${frames
          .map(frame => frame.frameNumber)
          .join(', ')}]`,
      );

      // Resolved before the state update, not inside it: a `setState` updater
      // runs in the render phase, so anything collected in there is still empty
      // when the lines below read it — and React expects the updater to be pure
      // anyway, invoking it twice under StrictMode.
      const resolved: Record<string, string> = {};
      const matched: string[] = [];
      for (const [speakerLabel, frameNumber] of Object.entries(requested)) {
        const url = urlByFrameNumber.get(frameNumber);
        if (url) {
          resolved[speakerLabel] = url;
          matched.push(speakerLabel);
        }
      }

      if (matched.length > 0) {
        setThumbnails(previous => ({ ...previous, ...resolved }));
        logger.debug(`Speaker thumbnails resolved for [${matched.join(', ')}]`);
      } else {
        logger.warn(
          `No returned frame matched a requested speaker. Requested ${JSON.stringify(
            requested,
          )}, received [${frames.map(f => f.frameNumber).join(', ')}]`,
        );
      }
    },
    [clearWatchdog],
  );

  const handleImagesProcessing = useCallback(() => {
    clearWatchdog();
    logger.warn(
      'Speaker frame thumbnails are still being generated for this story (CloudFront returned 403)',
    );
  }, [clearWatchdog]);

  const handleLoadError = useCallback(
    (error: string) => {
      clearWatchdog();
      logger.warn(`Failed to load speaker frame thumbnails: ${error}`);
    },
    [clearWatchdog],
  );

  const { loadFrames } = useFrameLoader({
    apiUrl: process.env.REACT_APP_API_URL || window.location.origin,
    onFramesLoaded: handleFramesLoaded,
    onImagesProcessing: handleImagesProcessing,
  });

  useEffect(() => {
    const frameNumbers = [...new Set(Object.values(frameNumbersBySpeaker))];
    if (frameNumbers.length === 0) return;

    if (!storyId) {
      logger.warn('No story id available, skipping speaker frame thumbnails');
      return;
    }

    const apiUrl = process.env.REACT_APP_API_URL || window.location.origin;
    logger.debug(
      `Requesting speaker frames [${frameNumbers.join(
        ', ',
      )}] for story ${storyId}`,
    );

    clearWatchdog();
    watchdogRef.current = setTimeout(() => {
      logger.warn(
        `Frame worker did not respond within ${
          WORKER_RESPONSE_TIMEOUT_MS / 1000
        }s for story ${storyId}. Check that /frameLoader.worker.js loaded and that GET ${apiUrl}/api/video/frames succeeded.`,
      );
    }, WORKER_RESPONSE_TIMEOUT_MS);

    loadFrames(storyId, storyId, frameNumbers, handleLoadError);

    return clearWatchdog;
  }, [
    storyId,
    frameNumbersBySpeaker,
    loadFrames,
    handleLoadError,
    clearWatchdog,
  ]);

  return thumbnails;
}
